<template>
  <div class="content">
    <section>
      <img
        :src="fullImg"
        :alt="`${label} map preview`"
        class="layer-detail-img"
      />
      <img
        :src="thumbImg"
        :alt="`${label} thumbnail`"
        class="layer-detail-thumb"
      />
      <div class="heading">
        <div class="title">
          <ys-text type="title3">
            {{ label }}
          </ys-text>
        </div>
        <ys-switch
          v-if="hasAccess && !isWindLayer"
          :selected="toggleValue"
          class="layer-detail-toggle"
          :data-test="`${id}-toggle`"
          @change="handleToggle"
        />
        <ys-switch
          v-else-if="hasWindAccess && isWindLayer"
          :selected="windState"
          class="layer-detail-toggle"
          :data-test="`${WIND_LAYER_INFO.id}-toggle`"
          @change="handleWindToggle"
        />
        <span v-else class="lock">
          <ys-icon
            icon="ysLock"
            data-test="lock"
            size="medium"
            class="lock-icon"
          />
        </span>
      </div>
      <component :is="detailsComponent" v-if="detailsComponent" />
    </section>
  </div>
</template>

<script setup lang="ts">
///// imports /////
import { computed, shallowRef, watchEffect, defineAsyncComponent } from 'vue'
import '@onxmaps/yellowstone-ui/ys-text'
import '@onxmaps/yellowstone-ui/ys-icon'
import '@onxmaps/yellowstone-ui/icons/ysLock'
import '@onxmaps/yellowstone-ui/ys-switch'
import type { YsSwitch } from '@onxmaps/yellowstone-ui/ys-switch'

import layerService from '@/services/layer-service'
import marketingEventService from '@/services/marketing-event-service'
import { getMode } from '@/lib/modes'
import { CARD_ORIGINS } from '@/lib/enums-marketing'

import { getItemRef, saveItem } from '@/plugins/storage'
import { injectXWeather } from '@/fish/providers/x-weather'
import { basemapMarketingName } from '@/services/basemap-service'
import { useHasAccess } from '@/services/feature-access/composables'
import { FishGates } from '@/services/feature-access/fish/types'

import { setWindLayer } from '@/fish/components/ui/wind/utils'

import { WIND_LAYER_INFO } from '@/fish/components/map-controls/utils'

///// types /////
type Props = {
  id: string
  label: string
  fullImg: string
  thumbImg: string
  component: string
  hasAccess: boolean
}

///// props/emits /////
const props = withDefaults(defineProps<Props>(), {
  id: '',
  label: '',
  fullImg: '',
  thumbImg: '',
  component: '',
  hasAccess: true,
})

///// refs and variables /////
const detailsComponent = shallowRef()
const xWeather = injectXWeather()
const windState = getItemRef<boolean>('wind-forecast-enabled')
const { hasAccess: hasWindAccess } = useHasAccess(FishGates.animated_wind)

///// computed /////
const toggleValue = computed(
  () => layerService.getLayerPreference(props.id)?.active
)

const mapMode = computed(getMode)

const isWindLayer = computed(() => props.id === 'wind')

///// functions /////
const sendMarketingEvent = (layerState: string, layerName: string) => {
  marketingEventService.publish(`layer_${layerState}`, {
    name: `layer_${layerState}`,
    origin: CARD_ORIGINS.LAYER_DETAILS,
    layer_name: layerName,
    map_mode: mapMode.value,
    active_basemap: basemapMarketingName.value,
  })
}

const handleToggle = (event: Event) => {
  const toggleState = (event.target as YsSwitch).selected
  layerService.toggleLayer(props.id)
  const layerState = toggleState ? 'enabled' : 'disabled'
  sendMarketingEvent(layerState, props.label)
}

const handleWindToggle = (event: Event) => {
  const toggleState = (event.target as YsSwitch).selected
  saveItem('wind-forecast-enabled', toggleState)
  const layerState = toggleState ? 'enabled' : 'disabled'
  sendMarketingEvent(layerState, WIND_LAYER_INFO.label)
  setWindLayer(toggleState, xWeather)
}

///// watchers /////
watchEffect(() => {
  detailsComponent.value = defineAsyncComponent(
    () => import(`@/components/layer-details/${props.component}`)
  )
})
</script>

<style lang="scss" scoped>
@use '@/styles/system/_shadow.scss';

.layer-detail-img {
  background-color: var(--ys-color-background-secondary);
  border-radius: var(--ys-radius-200) var(--ys-radius-200) 0px 0px;
  margin: 0px calc(-1 * var(--ys-spacing-250));
  min-width: calc(100% + 40px);
}

.layer-detail-thumb {
  width: 48px;
  position: relative;
  bottom: var(--ys-spacing-300);
  left: 0;
  border-radius: var(--ys-radius-100);
  box-shadow: shadow.$Low;
}

.content {
  background-color: var(--ys-color-background-secondary);
  padding: 0 var(--ys-spacing-250) var(--ys-spacing-250);
}

section {
  background-color: var(--ys-color-background-primary);
  padding: 0px var(--ys-spacing-250) var(--ys-spacing-250);
  border-radius: var(--ys-radius-200);
}

.heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--ys-spacing-250);
}

.title {
  display: flex;
  flex-direction: column;
  gap: var(--ys-spacing-25);
}

.layer-detail-toggle {
  margin-bottom: var(--ys-spacing-200);
}

.lock {
  border-radius: 50%;
  width: 35px;
  height: 35px;
  background-color: var(--ys-color-icon-twotone-primary);
  display: flex;
  justify-content: center;
  align-items: center;
}

.lock-icon {
  color: var(--ys-color-icon-twotone-secondary);
}
</style>
